import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { Calculator, Clock, Eye, Search, Copy, Trash2, RefreshCw } from 'lucide-react';

export default function SimulationsSidebar({
  calculating, result, savedSimulation, config, identite,
  isFtpeMember, simulations, filteredSimulations, simSearchQuery, setSimSearchQuery,
  handleLoadServerSimulation, handleDuplicateSimulation, handleDeleteSimulation,
}) {
  return (
    <div className="space-y-6">
      {/* Estimation Card */}
      <Card className="sticky top-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-emerald-600" />
            Estimation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {calculating ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          ) : (result || savedSimulation?.calcul) ? (
            <>
              <div className="text-center p-3 bg-gradient-to-r from-emerald-100 to-green-100 rounded-xl border-2 border-emerald-300">
                <div className="text-xs text-emerald-700 font-medium">MONTANT GLOBAL - CONTRIBUTION SOLIDAIRE PROPOSEE</div>
                <div className="text-2xl font-bold text-emerald-800">
                  {(savedSimulation?.calcul?.contribution_ht || savedSimulation?.calcul?.prime_ht || result?.contribution_ht || result?.calcul?.contribution_ht || 0).toLocaleString('fr-FR')} EUR
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Taux de base : {(savedSimulation?.calcul?.taux_final || savedSimulation?.calcul?.taux_base || result?.calcul?.taux_base || config?.taux_base || 1.2).toFixed(2)}%
                </div>
                {(result?.calcul?.tranches_actives || config?.tranches_taux_actives) && result?.calcul?.tranche_utilisee && (
                  <div className="mt-1 text-xs text-blue-600 bg-blue-50 rounded px-2 py-1 inline-block">
                    {result.calcul.tranche_utilisee.label || 'Tranche personnalisee'}
                  </div>
                )}
              </div>

              {!isFtpeMember && identite.qualite !== 'particulier' && (
                <div className="p-3 rounded-lg border-l-4 border-blue-600 bg-blue-50" data-testid="sidebar-ftpe-adhesion">
                  <div className="text-xs font-bold text-blue-700 uppercase mb-1">Adhesion partenaire</div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-700">Cotisation FTPE</span>
                    <span className="font-bold text-blue-700">200,00 EUR</span>
                  </div>
                </div>
              )}

              {!isFtpeMember && identite.qualite !== 'particulier' && (
                <div className="p-3 rounded-lg bg-indigo-900 text-white" data-testid="sidebar-ftpe-total">
                  <div className="text-xs font-bold uppercase mb-1">Total parcours cooperatif</div>
                  <div className="text-lg font-bold text-center">
                    {((savedSimulation?.calcul?.contribution_ht || savedSimulation?.calcul?.prime_ht || result?.contribution_ht || result?.calcul?.contribution_ht || 0) + 200).toLocaleString('fr-FR')} EUR
                  </div>
                </div>
              )}

              {(result?.calcul?.reductions?.length > 0 || savedSimulation?.calcul?.reductions?.length > 0) && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">Reductions appliquees :</div>
                  {(savedSimulation?.calcul?.reductions || result?.calcul?.reductions || []).map((r, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-gray-600">{r.motif}</span>
                      <span className="text-emerald-600 font-medium">-{r.taux}%</span>
                    </div>
                  ))}
                </div>
              )}

              <Separator />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Garantie</span>
                  <span className="font-medium">{result?.garantie?.duree_ans || savedSimulation?.calcul?.garantie?.duree_ans || 10} ans</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Effet</span>
                  <span className="font-medium">A reception</span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Renseignez les donnees techniques pour voir l'estimation</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historique des simulations */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Mes simulations
            <Badge variant="outline" className="ml-auto text-[10px]">{simulations.length}</Badge>
          </CardTitle>
          {simulations.length > 0 && (
            <div className="relative mt-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, reference ou montant..."
                value={simSearchQuery}
                onChange={(e) => setSimSearchQuery(e.target.value)}
                className="h-8 pl-8 text-xs"
                data-testid="sim-search-input"
              />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {simulations.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Aucune simulation</p>
          ) : filteredSimulations.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">Aucun resultat pour "{simSearchQuery}"</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredSimulations.map((sim) => (
                <div
                  key={sim.reference}
                  className="p-3 bg-gray-50 rounded-lg text-sm hover:bg-gray-100 transition-colors"
                >
                  <div className="flex justify-between items-start cursor-pointer" onClick={() => handleLoadServerSimulation(sim.reference)}>
                    <div>
                      <div className="font-medium text-emerald-700">{sim.reference}</div>
                      <div className="text-xs text-gray-500">{sim.nom}</div>
                    </div>
                    <Badge variant="outline" className="text-xs">{sim.status}</Badge>
                  </div>
                  <div className="mt-1 text-gray-600 cursor-pointer" onClick={() => handleLoadServerSimulation(sim.reference)}>
                    {sim.contribution_ht?.toLocaleString('fr-FR')} EUR
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-blue-600 flex items-center gap-1 cursor-pointer" onClick={() => handleLoadServerSimulation(sim.reference)}>
                      <Eye className="h-3 w-3" />
                      Charger
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                      data-testid={`duplicate-sim-${sim.reference}`}
                      onClick={(e) => { e.stopPropagation(); handleDuplicateSimulation(sim.reference); }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Dupliquer
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-sim-${sim.reference}`}
                      onClick={(e) => { e.stopPropagation(); handleDeleteSimulation(sim.reference); }}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
